import { CommandBarButton, ContextualMenu, DefaultButton, Dialog, DialogFooter, DialogType, ICommandBarStyles, IContextualMenuItem, IStackStyles, Label, PrimaryButton, SearchBox, Spinner, SpinnerSize, Stack, StackItem, Text } from "@fluentui/react";
import { useBoolean } from '@fluentui/react-hooks';

import styles from "./ChatHistoryPanel.module.css"
import { useContext } from "react";
import { AppStateContext } from "../../state/AppProvider";
import React from "react";
import ChatHistoryList from "./ChatHistoryList";
import { ChatHistoryLoadingState, historyDeleteAll } from "../../api";
import { useNavigate } from "react-router-dom";

interface ChatHistoryPanelProps {

}

export enum ChatHistoryPanelTabs {
    History = "History"
}

const commandBarStyle: ICommandBarStyles = {
    root: {
        padding: '0',
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: 'transparent'
    },
};

const commandBarButtonStyle: Partial<IStackStyles> = { root: { height: '50px' } };

export function ChatHistoryPanel(props: ChatHistoryPanelProps) {
    const appStateContext = useContext(AppStateContext)
    const [showContextualMenu, setShowContextualMenu] = React.useState(false);
    const [hideClearAllDialog, { toggle: toggleClearAllDialog }] = useBoolean(true);
    const [clearing, setClearing] = React.useState(false)
    const [clearingError, setClearingError] = React.useState(false)
    const navigate = useNavigate()
    const [keyword, setKeyword] = React.useState("")
    const [keywordDecided, setKeywordDecided] = React.useState("")

    const clearAllDialogContentProps = {
        type: DialogType.close,
        title: !clearingError? '本当にすべての履歴を削除しますか？' : 'チャット履歴の全削除中にエラーが発生しました。',
        closeButtonAriaLabel: 'Close',
        subText: !clearingError ? 'すべてのチャット履歴は完全に削除され、二度と復元できません。' : 'しばらくしてから再度お試しください。問題が解決しない場合はサイト管理者に連絡してください。',
    };
    
    const modalProps = {
        titleAriaId: 'labelId',
        subtitleAriaId: 'subTextId',
        isBlocking: true,
        styles: { main: { maxWidth: 450 } },
    }

    const menuItems: IContextualMenuItem[] = [
        { key: 'clearAll', text: 'すべてのチャット履歴を削除', iconProps: { iconName: 'Delete' }},
    ];

    const onShowContextualMenu = React.useCallback((ev: React.MouseEvent<HTMLElement>) => {
        ev.preventDefault(); // don't navigate
        setShowContextualMenu(true);
    }, []);

    const onHideContextualMenu = React.useCallback(() => setShowContextualMenu(false), []);

    const onClearAllChatHistory = async () => {
        setClearing(true)
        let ragIds = appStateContext?.state.selectedRAGIds? Array.from(appStateContext?.state.selectedRAGIds).sort():[]
        let response = await historyDeleteAll(ragIds)
        if(!response.ok){
            setClearingError(true)
        }else{
            appStateContext?.dispatch({ type: 'DELETE_CHAT_HISTORY', payload: new Set<number>(ragIds) })
            toggleClearAllDialog();
            navigate("/")
        }
        setClearing(false);
    }

    const onHideClearAllDialog = () => {
        toggleClearAllDialog()
        setTimeout(() => {
            setClearingError(false)
        }, 2000);
    }

    const handleKeyChange = (_ev?: React.ChangeEvent<HTMLElement>, newValue?:string) => {
        setKeyword(newValue || "")
    }
    const handleKeySearch = (newValue: string) =>{
        setKeywordDecided(keyword);
    }
    const handleKeyClear = () => {
        setKeyword("")
        setKeywordDecided("");
    }
    const titleText = appStateContext?.state.selectedRAGIds.size == 0 
                        ? "通常チャット"
                        : appStateContext?.state.RAGMenuList?.filter((v) => appStateContext?.state.selectedRAGIds.has(v.id)).map((v) => v.name).join(", ")

    React.useEffect(() => {}, [appStateContext?.state.chatHistory, clearingError]);

    return (
        <section className={styles.container} data-is-scrollable aria-label={"chat history panel"}>
            <div style={{
                marginTop: "15px",
                marginLeft: "10px",
                marginRight: "15px"
                }}>
                <SearchBox 
                    placeholder="会話タイトルを検索"
                    value={keyword}
                    onClear={handleKeyClear}
                    onChange={handleKeyChange}
                    onSearch={handleKeySearch}
                    >
                </SearchBox>
            </div>
            <div style={{
                display: "flex",
                flexWrap: "nowrap",
            }}>
                <Label 
                    style={{
                        minWidth: "0px", 
                        flexGrow: 1,
                        fontSize: "12pt",
                        display: "flex",
                        alignItems: "center"}}
                    >
                    <span style={{ 
                        textOverflow: "ellipsis", 
                        overflow: "hidden", 
                        whiteSpace: "nowrap",}}>
                        {titleText}
                    </span>
                </Label>
                <Stack verticalAlign="start">
                    <Stack horizontal styles={commandBarButtonStyle}>
                        <CommandBarButton
                            iconProps={{ iconName: 'More' }}
                            title={"Clear all chat history"}
                            onClick={onShowContextualMenu}
                            aria-label={"clear all chat history"}
                            styles={commandBarStyle}
                            role="button"
                            id="moreButton"
                        />
                        <ContextualMenu
                            items={menuItems}
                            hidden={!showContextualMenu}
                            target={"#moreButton"}
                            onItemClick={toggleClearAllDialog}
                            onDismiss={onHideContextualMenu}
                        />
                    </Stack>
                </Stack>
            </div>
            <Stack aria-label="chat history panel content"
                styles={{
                    root: {
                        display: "flex",
                        flexGrow: 1,
                        flexDirection: "column",
                        paddingTop: '2.5px',
                        maxWidth: "100%"
                    },
                }}
                style={{
                    display: "flex",
                    flexGrow: 1,
                    flexDirection: "column",
                    flexWrap: "wrap",
                    padding: "1px",
                    overflowX: "hidden",
                    overflowY: "auto"
                }}>
                {(appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Success && appStateContext?.state.isCosmosDBAvailable.cosmosDB) && <ChatHistoryList searchKeyword={keywordDecided}/>}
                {(appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Fail && appStateContext?.state.isCosmosDBAvailable) && <>
                    <Stack>
                        <Stack horizontalAlign='center' verticalAlign='center' style={{ width: "100%", marginTop: 10 }}>
                            <StackItem>
                                <Text style={{ alignSelf: 'center', fontWeight: '400', fontSize: 16 }}>
                                    {appStateContext?.state.isCosmosDBAvailable?.status ? <span>{appStateContext.state.isCosmosDBAvailable.status}</span> : <span>Error loading chat history</span>}
                                </Text>
                            </StackItem>
                            <StackItem>
                                <Text style={{ alignSelf: 'center', fontWeight: '400', fontSize: 14 }}>
                                    <span>Chat history can't be saved at this time</span>
                                </Text>
                            </StackItem>
                        </Stack>
                    </Stack>
                </>}
                {appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading && <>
                    <Stack>
                        <Stack horizontal horizontalAlign='center' verticalAlign='center' style={{ width: "95%" }}>
                            <StackItem style={{ justifyContent: 'center', alignItems: 'center' }}>
                                <Spinner style={{ alignSelf: "flex-start", height: "100%", marginRight: "5px" }} size={SpinnerSize.medium} />
                            </StackItem>
                            <StackItem>
                                <Text style={{ alignSelf: 'center', fontWeight: '400', fontSize: 14 }}>
                                    <span style={{ whiteSpace: 'pre-wrap' }}>ロード中</span>
                                </Text>
                            </StackItem>
                        </Stack>
                    </Stack>
                </>}
            </Stack>
            <Dialog
                hidden={hideClearAllDialog}
                onDismiss={clearing ? ()=>{} : onHideClearAllDialog}
                dialogContentProps={clearAllDialogContentProps}
                modalProps={modalProps}
            >
                <DialogFooter>
                {!clearingError && <PrimaryButton onClick={onClearAllChatHistory} disabled={clearing} text="削除" />}
                <DefaultButton onClick={onHideClearAllDialog} disabled={clearing} text={!clearingError ? "キャンセル" : "Close"} />
                </DialogFooter>
            </Dialog>
        </section>
    );
}